import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loading, SystemText } from '@salesperformanceint/e4shared';

import { setShowSuccess } from 'actions/ui';
import Dropdown from 'components/general/dropdown';
import NoResults from 'components/prism/noResults';
import Pathway from 'components/prism/pathway';
import Subheader from 'components/prism/subheader';
import Toggle from 'components/prism/toggle';
import { apiGet, apiPost } from 'services';
import Schedule from 'views/user/pathwayTracker/components/schedule';
import { pathwayStatuses } from 'helpers/pathway';

export const Pathways = ({ profile, setPathwayId }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [pathwayUsers, setPathwayUsers] = React.useState([]);
    const [pathwayUsersLoading, setPathwayUsersLoading] = React.useState(false);
    const [visiblePathways, setVisiblePathways] = React.useState([]);
    const [availablePathways, setAvailablePathways] = React.useState([]);
    const [view, setView] = React.useState('active');
    const [showEnrol, setShowEnrol] = React.useState(false);
    const [pathway, setPathway] = React.useState(null);
    const { user } = useSelector(state => state.user);
    const { prismMultiColumn } = useSelector(state => state.ui);

    const api = {
        loadAvaiablePathways: () => {
            apiGet('pathway-list-available', user.guid, response => {
                setAvailablePathways(response);
            });
        },
        loadProfilePathways: () => {
            apiGet(`team-member/pathways/${profile.id}`, user.guid, response => {
                setData(response);
                setLoading(false);
            });
        },
        assign: (details, callback) => {
            details.id = pathway.id;
            details.assigned_Ids = details.ids;
            apiPost('pathway-enroll', details, user.guid, () => {
                dispatch(setShowSuccess(SystemText('PathwayTracker/Schedule/Success')));
                setShowEnrol(false);
                api.loadProfilePathways();
                callback?.();
            });
        },
        users: () => {
            if (pathway) {
                setPathwayUsersLoading(true);
                apiGet(`pathway-users/${pathway.id}`, user.guid, response => {
                    setPathwayUsers(
                        response.map(u => ({
                            ...u,
                            disabled: u.date_Assigned && !u.date_Completed,
                        }))
                    );
                    setPathwayUsersLoading(false);
                });
            }
        },
    };

    React.useEffect(() => {
        api.loadProfilePathways();
    }, []);

    React.useEffect(() => {
        api.users();
    }, [pathway]);

    React.useEffect(() => {
        api.loadAvaiablePathways();
    }, []);

    React.useEffect(() => {
        setVisiblePathways(data.filter(x => x.status === pathwayStatuses[view]));
    }, [data, view]);

    const text = {
        enrol: SystemText('TeamMember/Pathway/Enrol'),
        active: SystemText('TeamMember/Pathway/Active'),
        completed: SystemText('TeamMember/Pathway/Completed'),
        selectPathway: SystemText('TeamMember/Pathway/Select'),
        activeNoResultsTitle: SystemText('TeamMember/Pathway/Active/NoResultsTitle', profile?.first_Name),
        activeNoResultsSummary: SystemText('TeamMember/Pathway/Active/NoResultsSummary', profile?.first_Name),
        completedNoResultsTitle: SystemText('TeamMember/Pathway/Completed/NoResultsTitle', profile?.first_Name),
        completedNoResultsSummary: SystemText('TeamMember/Pathway/Completed/NoResultsSummary', profile?.first_Name),
    };

    return (
        <>
            <div className="mt20">
                <div className="flex flex-center mb20">
                    <div className="flex-grow">
                        <Toggle value={view} onChange={setView}>
                            <button value="active" data-testid="toggle-active">
                                {text.active}
                            </button>
                            <button value="completed" data-testid="toggle-completed">
                                {text.completed}
                            </button>
                        </Toggle>
                    </div>
                    <Dropdown
                        id="pathway"
                        label={pathway?.name || text.selectPathway}
                        value={pathway?.id}
                        options={availablePathways?.map(x => {
                            return {
                                name: x.name,
                                value: x.id,
                                onClick: () => setPathway(x),
                            };
                        })}
                        anchorRight
                    />
                    <button className="ml10 btn btn-cornered btn-tertiary" onClick={() => setShowEnrol(true)}>
                        {pathwayUsersLoading && <i className="fa fa-spin fa-cog mr10" />}
                        {text.enrol}
                    </button>
                </div>
                {loading ? (
                    <Loading />
                ) : (
                    <>
                        <Subheader title={view === 'active' ? text.active : text.completed} count={visiblePathways.length} />
                        {visiblePathways.length === 0 ? (
                            <div>
                                <NoResults
                                    icon="route"
                                    title={view === 'active' ? text.activeNoResultsTitle : text.completedNoResultsTitle}
                                    summary={
                                        view === 'active' ? text.activeNoResultsSummary : text.completedNoResultsSummary
                                    }
                                />
                            </div>
                        ) : (
                            <div className={prismMultiColumn ? 'even-col' : ''}>
                                {visiblePathways.map(item => (
                                    <Pathway
                                        key={`pathway-${item.id}`}
                                        item={item}
                                        onClick={() => setPathwayId(item.id)}
                                        padBottom={!prismMultiColumn}
                                        managerView
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
            <Schedule
                show={showEnrol}
                pathway={pathway}
                users={pathwayUsers}
                close={() => setShowEnrol(false)}
                onSchedule={api.assign}
            />
        </>
    );
};

export default Pathways;
